import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Check, Calendar } from "lucide-react";

export const ResultsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const results = [
    "Un système de contenu qui te ressemble (sans te cramer)",
    "Des hooks + posts + carrousels prêts, alignés et vendables",
    "Une page claire (même si tu détestes \"les pages de vente\")",
    "Un tunnel Stories (séquence) pour convertir sans te sentir agressif",
    "Des scripts et messages qui respectent tes valeurs et augmentent la conversion",
    "Des protocoles simples pour tes clients entre les séances (avec prudence & cadre)",
    "Une routine et un plan pour rester régulier(ère) sans dépendre de ta motivation",
  ];

  return (
    <section 
      ref={ref}
      className="section-padding bg-gradient-to-b from-mint-whisper to-white relative overflow-hidden"
      data-testid="results-section"
    >
      <div className="lumen-container">
        <div className="max-w-4xl mx-auto">
          {/* Section header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="font-cinzel text-sm tracking-[0.3em] text-gold-metallic uppercase mb-4 block">
              Résultats concrets
            </span>
            <h2 className="section-title font-cormorant text-text-main mb-4">
              Ce que tu vas obtenir
            </h2>
            <p className="font-manrope text-text-muted">
              Sans promesses magiques — juste des résultats tangibles
            </p>
          </motion.div>

          {/* Time badge */}
          <motion.div
            className="flex justify-center mb-12"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="inline-flex items-center gap-3 glass-card px-6 py-3">
              <Calendar className="w-5 h-5 text-emerald-600" />
              <span className="font-manrope font-medium text-emerald-700">
                En 7 jours, tu peux avoir :
              </span>
            </div>
          </motion.div>

          {/* Results list */}
          <motion.div
            className="glass-card p-8 md:p-12"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <ul className="space-y-5">
              {results.map((result, index) => (
                <motion.li
                  key={index}
                  className="flex items-start gap-4"
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                >
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="font-manrope text-text-main leading-relaxed">
                    {result}
                  </span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Bottom message */}
          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <p className="font-cormorant text-xl md:text-2xl text-text-main max-w-2xl mx-auto">
              Tu n'as pas besoin d'aimer le marketing.
              <br />
              <span className="text-emerald-700 font-medium">
                Tu as besoin d'un système qui honore ton don et ton compte bancaire.
              </span>
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
